import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useAuth } from "../../hooks/useAuth";
import Loading from "../common/Loading";
import Button from "../common/Button";
import IssueCard from "./IssueCard";
import CreateIssue from "./CreateIssue";
import IssueFilters from "./IssueFilters";
import IssueView from "./IssueView";
import { useNavigate } from "react-router-dom";

const Issues = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    status: "all",
    priority: "all",
    assignee: "all",
    project: "all",
  });
  const [view, setView] = useState("list");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchIssues = async () => {
      if (!userProfile) return;

      try {
        // Get user's projects first
        const projectsQuery = query(
          collection(db, "projects"),
          where("members", "array-contains", userProfile.uid)
        );
        const projectsSnapshot = await getDocs(projectsQuery);
        const projectIds = projectsSnapshot.docs.map((doc) => doc.id);

        if (projectIds.length === 0) {
          setIssues([]);
          return;
        }

        // Get issues from user's projects
        const issuesQuery = query(
          collection(db, "issues"),
          where("projectId", "in", projectIds),
          orderBy("createdAt", "desc")
        );
        const snapshot = await getDocs(issuesQuery);
        const issuesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setIssues(issuesData);
        setFilteredIssues(issuesData);
      } catch (error) {
        console.error("Error fetching issues:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchIssues();
  }, [userProfile]);

  const handleIssueCreated = (newIssue) => {
    setIssues((prev) => [newIssue, ...prev]);
    setFilteredIssues((prev) => [newIssue, ...prev]);
    setShowCreateModal(false);
  };

  const handleIssueDeleted = (issueId) => {
    setIssues((prev) => prev.filter((issue) => issue.id !== issueId));
    setFilteredIssues((prev) => prev.filter((issue) => issue.id !== issueId));
  };

  const handleIssueUpdated = (updatedIssue) => {
  console.log('Updating issue in list:', updatedIssue.id, updatedIssue.type, updatedIssue.status);
  
  setIssues(prev => prev.map(issue => 
    issue.id === updatedIssue.id ? { ...issue, ...updatedIssue } : issue
  ));
  setFilteredIssues(prev => prev.map(issue => 
    issue.id === updatedIssue.id ? { ...issue, ...updatedIssue } : issue
  ));
};


  const applyFilters = (newFilters) => {
    setFilters(newFilters);

    let filtered = issues;

    if (newFilters.status !== "all") {
      filtered = filtered.filter((issue) => issue.status === newFilters.status);
    }

    if (newFilters.priority !== "all") {
      filtered = filtered.filter(
        (issue) => issue.priority === newFilters.priority
      );
    }

    if (newFilters.assignee !== "all") {
      filtered = filtered.filter(
        (issue) => issue.assigneeId === newFilters.assignee
      );
    }

    if (newFilters.project !== "all") {
      filtered = filtered.filter(
        (issue) => issue.projectId === newFilters.project
      );
    }

    setFilteredIssues(filtered);
  };

  if (loading) {
    return <Loading text="Loading issues..." />;
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
          Issues
        </h1>
        <div className="flex flex-col xs:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden"
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            variant="primary"
            className="flex-1 xs:flex-none"
          >
            Create Issue
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Filters - Hidden on mobile by default */}
        <div className={`lg:w-64 ${showFilters ? "block" : "hidden lg:block"}`}>
          <IssueFilters
            filters={filters}
            onFiltersChange={applyFilters}
            issues={issues}
            onClose={() => setShowFilters(false)}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <IssueView
              view={view}
              onViewChange={setView}
              issueCount={filteredIssues.length}
            />
            <div className="flex items-center justify-between sm:justify-end">
              <span className="text-sm lg:text-base text-gray-500 dark:text-gray-400 sm:hidden">
                {filteredIssues.length} issue
                {filteredIssues.length !== 1 ? "s" : ""}
              </span>
              <span className="hidden sm:inline text-sm lg:text-base text-gray-500 dark:text-gray-400">
                {filteredIssues.length} issue
                {filteredIssues.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>

          {filteredIssues.length === 0 ? (
            <div className="text-center py-8 lg:py-12">
              <div className="text-gray-400 dark:text-gray-500 text-6xl mb-4">
                📋
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No issues found
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {issues.length === 0
                  ? "Create your first issue to get started"
                  : "Try adjusting your filters"}
              </p>
              {issues.length === 0 && (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  variant="primary"
                >
                  Create Issue
                </Button>
              )}
            </div>
          ) : view === "list" ? (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block space-y-4">
                {filteredIssues.map((issue) => (
                  <IssueCard
                    key={issue.id}
                    issue={issue}
                    onDelete={handleIssueDeleted}
                    onUpdate={handleIssueUpdated}
                  />
                ))}
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-3">
                {filteredIssues.map((issue) => (
                  <div
                    key={issue.id}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 dark:text-white truncate">
                          {issue.issueKey}: {issue.title}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {issue.description || "No description"}
                        </p>
                      </div>
                      <span
                        className={`ml-2 px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
                          issue.status === "todo"
                            ? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                            : issue.status === "in-progress"
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                            : issue.status === "in-review"
                            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                            : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                        }`}
                      >
                        {issue.status.replace("-", " ")}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-500 dark:text-gray-400">
                          Type:
                        </span>
                        <p className="mt-1 text-gray-900 dark:text-white capitalize">
                          {issue.type}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-500 dark:text-gray-400">
                          Priority:
                        </span>
                        <span
                          className={`mt-1 inline-block px-2 py-1 text-xs font-medium rounded-full ${
                            issue.priority === "low"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                              : issue.priority === "medium"
                              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                              : issue.priority === "high"
                              ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300"
                              : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                          }`}
                        >
                          {issue.priority}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                      <div className="flex justify-between items-center text-sm">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">
                            Assignee:
                          </span>
                          <span className="ml-2 text-gray-900 dark:text-white">
                            {issue.assigneeName || "Unassigned"}
                          </span>
                        </div>
                        <div className="text-gray-500 dark:text-gray-400">
                          {issue.createdAt
                            ? new Date(issue.createdAt).toLocaleDateString()
                            : "Unknown"}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex space-x-2">
                      <Button
                        variant="outline"
                        size="small"
                        onClick={() => navigate(`/issues/${issue.id}`)} // Add this handler
                        className="flex-1"
                      >
                        View
                      </Button>
                      <Button
                        variant="danger"
                        size="small"
                        onClick={() => handleIssueDeleted(issue.id)}
                        className="flex-1"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <KanbanView
              issues={filteredIssues}
              onIssueUpdate={handleIssueUpdated}
            />
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateIssue
          onClose={() => setShowCreateModal(false)}
          onIssueCreated={handleIssueCreated}
        />
      )}
    </div>
  );
};

const KanbanView = ({ issues, onIssueUpdate }) => {
  const statusColumns = [
    { key: "todo", title: "To Do", color: "gray" },
    { key: "in-progress", title: "In Progress", color: "blue" },
    { key: "in-review", title: "In Review", color: "yellow" },
    { key: "done", title: "Done", color: "green" },
  ];

  const getIssuesByStatus = (status) => {
    return issues.filter((issue) => issue.status === status);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
      {statusColumns.map((column) => (
        <div
          key={column.key}
          className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 lg:p-4"
        >
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm lg:text-base">
              {column.title}
            </h3>
            <span className="bg-white dark:bg-gray-700 px-2 py-1 rounded text-xs lg:text-sm">
              {getIssuesByStatus(column.key).length}
            </span>
          </div>
          <div className="space-y-2 lg:space-y-3">
            {getIssuesByStatus(column.key).map((issue) => (
              <IssueCard
                key={issue.id}
                issue={issue}
                compact
                onUpdate={onIssueUpdate}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Issues;
